import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Loader2, Trophy, Music, Video, Users, Share, Plus } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { apiRequest } from '@/lib/queryClient';
import type { LeagueData } from './hooks/use-league-chat';
import type { CommunityContext } from './chat-input';

// Re-export for convenience
export type { LeagueData } from './hooks/use-league-chat';

interface LeaguePickerProps {
  onSelect: (league: LeagueData) => void;
  disabled?: boolean;
  communityContext?: CommunityContext;
}

interface LeagueResponse {
  id: number;
  name: string;
  description: string | null;
  type: string;
  category: string | null;
  headerImageUrl: string | null;
  themeColor: string;
  is_public: boolean;
  inviteCode: string | null;
  status: string;
  maxParticipants: number;
  memberCount?: number;
  participantCount?: number;
  participants?: number;
  owner?: { username: string } | null;
  ownerUsername?: string;
  membersCanInvite?: boolean;
  isOwner?: boolean;
}

export function LeaguePicker({ onSelect, disabled, communityContext }: LeaguePickerProps) {
  const [open, setOpen] = useState(false);
  const [leagues, setLeagues] = useState<LeagueResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, navigate] = useLocation();

  useEffect(() => {
    if (open && leagues.length === 0) {
      fetchLeagues();
    }
  }, [open]);

  const fetchLeagues = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiRequest<LeagueResponse[]>('/api/leagues/user/active?lite=1');
      // Filter to leagues that are shareable (active, not completed)
      let shareable = (response || []).filter(
        (l) => l.status !== 'completed' && l.status !== 'cancelled'
      );
      // In scene context, sort matching-category leagues first
      if (communityContext?.channelName) {
        const categoryName = communityContext.channelName.toLowerCase();
        shareable.sort((a, b) => {
          const aMatch = a.category?.toLowerCase() === categoryName ? 0 : 1;
          const bMatch = b.category?.toLowerCase() === categoryName ? 0 : 1;
          return aMatch - bMatch;
        });
      }
      setLeagues(shareable);
    } catch (err) {
      console.error('Failed to fetch leagues:', err);
      setError('Failed to load leagues');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeagueSelect = (league: LeagueResponse) => {
    const memberCount = league.memberCount ?? league.participantCount ?? league.participants ?? 0;
    // Only include inviteCode if user is owner or membersCanInvite is true
    const canShareInvite = league.isOwner || league.membersCanInvite !== false;

    onSelect({
      leagueId: league.id,
      name: league.name || 'League',
      description: league.description ?? null,
      headerImageUrl: league.headerImageUrl ?? null,
      themeColor: league.themeColor || '#5D3FD3',
      type: (league.type as 'music' | 'video') || 'music',
      category: league.category ?? null,
      participants: memberCount,
      maxParticipants: league.maxParticipants || 10,
      isPublic: league.is_public ?? false,
      inviteCode: canShareInvite ? (league.inviteCode ?? null) : null,
      status: league.status || 'active',
      ownerUsername: league.owner?.username || league.ownerUsername || null,
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={disabled}
        >
          <Trophy className="h-4 w-4" />
          League
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="top">
        <div className="flex flex-col max-h-[400px]">
          {/* Header */}
          <div className="p-3 border-b flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            <span className="font-medium">Share a League</span>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: '320px' }}>
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                {error}
              </div>
            ) : leagues.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-sm text-muted-foreground p-4 text-center">
                <Trophy className="h-8 w-8 mb-2 opacity-50" />
                <p>No leagues to share</p>
                <p className="text-xs mt-1">Join or create a league first</p>
              </div>
            ) : (
              <div className="divide-y">
                {leagues.map((league) => {
                  const memberCount = league.memberCount ?? league.participantCount ?? league.participants ?? 0;
                  const TypeIcon = league.type === 'video' ? Video : Music;
                  return (
                    <button
                      key={league.id}
                      onClick={() => handleLeagueSelect(league)}
                      className="w-full p-3 hover:bg-muted/50 transition-colors flex items-center gap-3 text-left"
                    >
                      {/* League image */}
                      <div
                        className="w-10 h-10 flex-shrink-0 rounded overflow-hidden"
                        style={{ backgroundColor: league.themeColor || '#5D3FD3' }}
                      >
                        {league.headerImageUrl ? (
                          <img
                            src={league.headerImageUrl}
                            alt={league.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Trophy className="h-5 w-5 text-white/80" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm">{league.name}</p>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <TypeIcon className="h-3 w-3" />
                          <Users className="h-3 w-3" />
                          <span>{memberCount}/{league.maxParticipants}</span>
                          {!league.is_public && (
                            <span className="text-[10px] bg-muted px-1 rounded">Invite</span>
                          )}
                        </div>
                      </div>

                      {/* Share icon */}
                      <div className="flex-shrink-0">
                        <Share className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Create New League button (only in community context) */}
          {communityContext && (
            <div className="p-2 border-t">
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1.5"
                onClick={() => {
                  setOpen(false);
                  const params = new URLSearchParams();
                  if (communityContext.categoryType) params.set('type', communityContext.categoryType);
                  if (communityContext.categoryId) params.set('category', communityContext.categoryId.toString());
                  params.set('sceneChannel', communityContext.channelId.toString());
                  params.set('channelName', communityContext.channelName);
                  navigate(`/create?${params.toString()}`);
                }}
              >
                <Plus className="h-4 w-4" />
                Create New League
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
